import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, User, Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "For Students", to: "/for-students", testid: "navbar-students-link" },
  { label: "For Employers", to: "/employers", testid: "navbar-employers-link" },
  { label: "For Institutions", to: "/institutions", testid: "navbar-institutions-link" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isIndividual = user?.role === "professional";
  const [open, setOpen] = useState(false);

  // Close mobile menu whenever route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock page scroll while mobile drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-ec-line" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-10 h-16 flex items-center justify-between gap-3">
        {/* Left: hamburger on mobile/tablet, logo on md+ */}
        {!user && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={open}
            aria-controls="mobile-nav-drawer"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-1.5 rounded-sm hover:bg-ec-mute text-ec-ink"
            data-testid="navbar-menu-button"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <Link
          to={user ? "/dashboard" : "/"}
          className={user ? "" : "hidden md:inline-flex"}
          data-testid="navbar-logo"
        >
          <Logo size={34} />
        </Link>

        {/* Desktop nav links — hidden on mobile/tablet */}
        {!user && (
          <nav className="hidden md:flex items-center gap-6 text-sm" data-testid="navbar-desktop-nav">
            {NAV_ITEMS.map((it) =>
              it.external ? (
                <a key={it.label} href={it.href} className="hover:text-ec-emerald transition-colors">{it.label}</a>
              ) : (
                <NavLink
                  key={it.label}
                  to={it.to}
                  className="hover:text-ec-emerald transition-colors flex items-center gap-1"
                  data-testid={it.testid}
                >
                  {it.icon && <it.icon className="w-4 h-4" />} {it.label}
                </NavLink>
              ),
            )}
          </nav>
        )}

        {/* Right cluster */}
        <div className="flex items-center gap-2 ml-auto">
          {!user ? (
            <>
              <Link to="/login" data-testid="navbar-login-link">
                <Button variant="ghost" className="rounded-sm h-9 px-3 text-sm">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="navbar-register-link">
                <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 h-9 px-3 text-sm">
                  <span className="hidden sm:inline">Get Started →</span>
                  <span className="sm:hidden">Get Started</span>
                </Button>
              </Link>
            </>
          ) : (
            <>
              <NotificationBell />
              <Link
                to={isIndividual ? "/passport" : "/org-profile"}
                className="p-2 rounded-sm hover:bg-ec-mute"
                aria-label="Profile"
                data-testid="navbar-profile-button"
              >
                <User className="w-4 h-4" />
              </Link>
              <button
                type="button"
                aria-label="Logout"
                onClick={() => { logout(); nav("/"); }}
                className="p-2 rounded-sm hover:bg-ec-mute"
                data-testid="navbar-logout-button"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile slide-in drawer (md and below). Only rendered for signed-out marketing nav. */}
      {!user && createPortal(
        <MobileDrawer open={open} onClose={() => setOpen(false)} />,
        document.body,
      )}
    </header>
  );
}

function MobileDrawer({ open, onClose }) {
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`md:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      />
      {/* Panel */}
      <aside
        id="mobile-nav-drawer"
        role="dialog"
        aria-label="Site navigation"
        className={`md:hidden fixed left-0 top-0 z-50 h-full w-[82%] max-w-sm bg-white border-r border-ec-line shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-nav-drawer"
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-ec-line">
          <Logo size={30} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex items-center justify-center w-9 h-9 rounded-sm hover:bg-ec-mute text-ec-ink"
            data-testid="navbar-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex flex-col px-2 py-3" data-testid="mobile-nav-list">
          {NAV_ITEMS.map((it) =>
            it.external ? (
              <a
                key={it.label}
                href={it.href}
                onClick={onClose}
                className="px-4 py-3 text-base font-medium text-ec-ink hover:bg-ec-mute rounded-sm"
              >
                {it.label}
              </a>
            ) : (
              <NavLink
                key={it.label}
                to={it.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `px-4 py-3 text-base font-medium rounded-sm transition-colors flex items-center gap-2 ${
                    isActive ? "bg-ec-mute text-ec-emerald" : "text-ec-ink hover:bg-ec-mute"
                  }`
                }
                data-testid={`mobile-${it.testid}`}
              >
                {it.icon && <it.icon className="w-4 h-4" />} {it.label}
              </NavLink>
            ),
          )}
        </nav>
        <div className="absolute inset-x-0 bottom-0 p-4 border-t border-ec-line bg-ec-soft flex flex-col gap-2">
          <Link to="/login" onClick={onClose} data-testid="mobile-navbar-login-link">
            <Button variant="outline" className="w-full rounded-sm border-ec-line">Sign in</Button>
          </Link>
          <Link to="/register" onClick={onClose} data-testid="mobile-navbar-register-link">
            <Button className="w-full rounded-sm bg-ec-ink hover:bg-zinc-800">Get Started →</Button>
          </Link>
        </div>
      </aside>
    </>
  );
}
